import React, { useState } from 'react';
import { Typography, Box, Link } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from '@mui/styles';
import { FormattedMessage } from 'react-intl';
import { ConnectorInfo } from 'types/types';
import { EmailNotificationDialog } from './EmailNotificationDialog';

const useStyles = makeStyles((theme) => ({
  box: {
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: theme.spacing(28),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    border: '1px solid',
    borderColor: theme.palette.info.main
  },
  needHelp: {
    fontFamily: theme.typography.fontFamily,
    padding: theme.spacing(2, 0, 0, 2)
  },
  button: {
    textDecoration: 'underline',
    fontFamily: theme.typography.fontFamily
  },
  link: { padding: theme.spacing(1.75) },
  icon: {
    fontSize: theme.spacing(2),
    marginLeft: theme.spacing(0.65)
  },
  text: {
    display: 'inline',
    textDecoration: 'underline'
  }
}));

type ConnectHelpTemplateProps = {
  info: ConnectorInfo;
};

export const ConnectHelpTemplate: React.FC<ConnectHelpTemplateProps> = ({
  info
}: ConnectHelpTemplateProps) => {
  const classes = useStyles();
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);

  const handleClick = () => {
    setNotificationDialogOpen(true);
  };

  const handleEmailDialogClose = () => {
    setNotificationDialogOpen(false);
  };
  return (
    <Box className={classes.box} data-testid="check-needHelp-components">
      <div>
        <Typography className={classes.needHelp} data-testid="needHelpText">
          <FormattedMessage id={'needHelpSection.needHelp'} />
        </Typography>
        <div className={classes.link} data-testid="check-links">
          {info.repliconDocumentationLink && (
            <Link
              href={info.repliconDocumentationLink}
              target="_blank"
              underline="none"
              data-testid="check-document-link"
            >
              <Typography className={classes.text} data-testid="document-text">
                <FormattedMessage id={'needHelpSection.documentation'} />
              </Typography>
              <OpenInNewIcon
                color="primary"
                className={classes.icon}
                data-testid="external-link-icon1"
              />
            </Link>
          )}
          {info.polarisDocumentationLink && (
            <Link
              href={info.polarisDocumentationLink}
              target="_blank"
              underline="none"
              data-testid="check-polaris-document"
            >
              <Typography
                className={classes.text}
                data-testid="polaris-document-text"
              >
                <FormattedMessage id={'needHelpSection.documentationPolaris'} />
              </Typography>
              <OpenInNewIcon
                color="primary"
                className={classes.icon}
                data-testid="external-link-icon2"
              />
            </Link>
          )}

          <Link
            component="button"
            variant="body1"
            onClick={handleClick}
            className={classes.button}
            data-testid="needHelp-button"
          >
            <FormattedMessage id={'needHelpSection.emailNotification'} />
          </Link>
        </div>
      </div>
      <div data-testid="emailNotificationDialogContent">
        {notificationDialogOpen && (
          <EmailNotificationDialog
            info={info}
            open={notificationDialogOpen}
            handleEmailDialogClose={handleEmailDialogClose}
          />
        )}
      </div>
    </Box>
  );
};
